import React, { useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import VoiceInput from '../components/VoiceInput'
import AssistantChat from '../components/AssistantChat'
import TransactionList from '../components/TransactionList'
import { useWeb3Wallet } from '../contexts/Web3WalletProvider'
import { useVoice } from '../contexts/VoiceContext'
import { useTransaction } from '../contexts/TransactionContext'

const HomePage: React.FC = () => {
  const { 
    isConnected, 
    address, 
    balance, 
    tokenBalances,
    claimVoiceAirdrop,
    hasClaimedAirdrop
  } = useWeb3Wallet()
  const { isListening, isProcessing, isSpeaking } = useVoice()
  const { transactions } = useTransaction()
  const [greeting, setGreeting] = useState('')
  const [showClaimModal, setShowClaimModal] = useState(false)
  const [claimCode, setClaimCode] = useState('')
  const [isClaimingAirdrop, setIsClaimingAirdrop] = useState(false)

  // Removed auto-scroll to prevent forced scrolling behavior

  useEffect(() => {
    const hour = new Date().getHours()
    if (hour < 12) {
      setGreeting('Good morning')
    } else if (hour < 18) {
      setGreeting('Good afternoon')
    } else {
      setGreeting('Good evening')
    }
  }, [])

  const recentTransactions = transactions.slice(0, 3)
  const pendingTransactions = transactions.filter(tx => tx.status === 'pending')

  const formatBalance = (balance: string) => {
    const num = parseFloat(balance)
    if (num < 0.001) {
      return balance
    }
    return num.toFixed(4)
  }

  const formatAddress = (address: string | null) => {
    if (!address) return 'Not connected'
    return `${address.slice(0, 6)}...${address.slice(-4)}`
  }

  const getVoiceStatus = () => {
    if (isListening) return 'Listening...'
    if (isProcessing) return 'Processing...'
    if (isSpeaking) return 'Speaking...'
    return 'Ready to listen'
  }

  const handleClaimAirdrop = async () => {
    if (!claimCode.trim()) {
      alert('Please enter a valid claim code')
      return
    }

    if (!isConnected) {
      alert('Please connect your wallet first')
      return
    }

    setIsClaimingAirdrop(true)
    try {
      // Check if user has already claimed
      const hasClaimed = await hasClaimedAirdrop()
      if (hasClaimed) {
        alert('You have already claimed your airdrop!')
        return
      }

      // Get claim data with signature from Telegram bot API
      const botUrl = import.meta.env.VITE_TELEGRAM_BOT_URL || 'https://voice-telegram-bot.your-subdomain.workers.dev'
      console.log('Fetching claim data from:', `${botUrl}/api/get-claim-data`)
      
      const response = await fetch(`${botUrl}/api/get-claim-data`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          claimCode: claimCode.trim(),
          userAddress: address
        })
      })
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }
      
      const result = await response.json()
      console.log('Claim data result:', result)
      
      if (!result.valid) {
        alert(`Invalid claim code: ${result.error || 'Please check your code and try again.'}`)
        return
      }
      
      // Check if VOICE token contract is configured
      const voiceTokenAddress = import.meta.env.VITE_VOICE_TOKEN_ADDRESS
      if (!voiceTokenAddress || voiceTokenAddress === '0x0000000000000000000000000000000000000000') {
        alert('❌ VOICE token contract not configured. Please contact support.')
        return
      }
      
      console.log('Claiming airdrop with:', { claimCode: claimCode.trim(), tier: result.tier, signature: result.signature })
      
      // Claim airdrop through smart contract
      const claimResult = await claimVoiceAirdrop(claimCode.trim(), result.tier, result.signature)
      
      if (claimResult.success) {
        alert(`🎉 Airdrop claimed successfully!\n\n💰 ${result.amount.toLocaleString()} VOICE tokens (${result.tierName} tier)\n📝 Transaction: ${claimResult.hash}\n\n✅ Tokens have been added to your wallet!`)
        setShowClaimModal(false)
        setClaimCode('')
      } else {
        console.error('Claim failed:', claimResult.error)
        alert(`❌ Failed to claim airdrop: ${claimResult.error || 'Unknown error occurred'}`)
      }
    } catch (error: any) {
      console.error('Failed to claim airdrop:', error)
      let errorMessage = 'Failed to claim airdrop. '
      
      if (error.message?.includes('HTTP')) {
        errorMessage += 'Unable to connect to claim service. '
      } else if (error.message?.includes('fetch')) {
        errorMessage += 'Network connection error. '
      } else if (error.message?.includes('contract')) {
        errorMessage += 'Smart contract error. '
      } else {
        errorMessage += 'Unknown error occurred. '
      }
      
      errorMessage += 'Please check your internet connection and try again.'
      alert(errorMessage)
    } finally {
      setIsClaimingAirdrop(false)
    }
  }

  const getVoiceStatusColor = () => {
    if (isListening) return 'text-blue-400'
    if (isProcessing) return 'text-yellow-400'
    if (isSpeaking) return 'text-green-400'
    return 'text-white/70'
  }

  return (
    <motion.div 
      className="space-y-6 pt-4"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
    >
      {/* Welcome Section */}
      <div className="text-center">
        <h1 className="text-2xl font-bold text-white mb-2">
          {greeting}! 👋
        </h1>
        <p className="text-white/70 text-sm">
          Ready to make payments with your voice?
        </p>
      </div>

      {/* Wallet Status */}
      {isConnected && (
        <div className="bg-gray-900/95 backdrop-blur-sm rounded-2xl p-4 border border-gray-700/50">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-yellow-500 rounded-full flex items-center justify-center">
                <img src="/Voice-32x32.svg" alt="Voice" className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-white font-medium">Wallet Connected</h3>
                <p className="text-white/70 text-sm">{formatAddress(address)}</p>
              </div>
            </div>
            <div className="text-right">
              <div className="text-lg font-bold text-white">
                {formatBalance(balance)} BNB
              </div>
              <p className="text-white/70 text-xs">BNB CHAIN</p>
            </div>
          </div>
          
          {/* Token Balances */}
          {tokenBalances.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-white/70 text-xs font-medium">Token Balances</h4>
              <div className="grid grid-cols-1 gap-2">
                {tokenBalances.map((token, index) => (
                  <div key={index} className="bg-gray-800/80 rounded-xl p-3 flex justify-between items-center">
                    <span className="text-white/70 text-sm">{token.symbol}</span>
                    <span className="text-white font-medium text-sm">{formatBalance(token.balance)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Quick Stats Row */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-gray-800/80 rounded-xl p-3">
              <div className="flex items-center space-x-2 mb-1">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span className="text-white/70 text-xs">Recent</span>
              </div>
              <div className="text-white font-medium">{recentTransactions.length}</div>
            </div>
            <div className="bg-gray-800/80 rounded-xl p-3">
              <div className="flex items-center space-x-2 mb-1">
                <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                <span className="text-white/70 text-xs">Pending</span>
              </div>
              <div className="text-white font-medium">{pendingTransactions.length}</div>
            </div>
          </div>
        </div>
      )}
      
      {/* Voice Status */}
      <div className="bg-gray-900/95 backdrop-blur-sm rounded-2xl p-4 border border-gray-700/50">
        <div className="flex items-center justify-center space-x-3">
          {isListening ? (
            <div className="w-3 h-3 bg-blue-500 rounded-full animate-pulse"></div>
          ) : isProcessing ? (
            <div className="w-3 h-3 bg-yellow-500 rounded-full animate-pulse"></div>
          ) : isSpeaking ? (
            <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
          ) : (
            <div className="w-3 h-3 bg-gray-500 rounded-full"></div>
          )}
          <span className={`text-sm font-medium ${getVoiceStatusColor()}`}>
            {getVoiceStatus()}
          </span>
        </div>
      </div>
      
      {/* Voice Input Section */}
      <div className="bg-gray-900/95 backdrop-blur-sm rounded-2xl p-4 border border-gray-700/50">
        <div className="flex items-center space-x-3 mb-4">
          <div className="w-10 h-10 bg-yellow-500 rounded-full flex items-center justify-center">
            <img src="/Voice-32x32.svg" alt="Voice" className="w-5 h-5" />
          </div>
          <h2 className="text-lg font-bold text-white">Voice Commands</h2>
        </div>
        <VoiceInput />
      </div>
      
      {/* Assistant Chat */}
      <div className="bg-gray-900/95 backdrop-blur-sm rounded-2xl p-4 border border-gray-700/50">
        <div className="flex items-center space-x-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-yellow-500 flex items-center justify-center">
            <img src="/Voice-32x32.svg" alt="Voice" className="w-5 h-5" />
          </div>
          <h2 className="text-lg font-bold text-white">Assistant</h2>
        </div>
        <AssistantChat />
      </div>
      
      {/* Quick Commands */}
      <div className="bg-gray-900/95 backdrop-blur-sm rounded-2xl p-4 border border-gray-700/50">
        <h3 className="text-white font-medium mb-3">Try saying:</h3>
        <div className="space-y-2">
          {[
            'Send 0.1 BNB to 0xabc123...',
            'What is my balance?',
            'Show transaction history',
            'Send 100 USDC to Jessica'
          ].map((command, index) => (
            <div
              key={index}
              className="p-3 rounded-xl bg-white/5 border border-white/10 text-white/80 text-sm"
            >
              "{command}"
            </div>
          ))}
        </div>
      </div>

      {/* Recent Transactions */}
      {isConnected && recentTransactions.length > 0 && (
        <div className="bg-gray-900/95 backdrop-blur-sm rounded-2xl p-4 border border-gray-700/50">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-white">Recent Transactions</h2>
            <button className="text-blue-400 hover:text-blue-300 text-sm font-medium">
              View All
            </button>
          </div>
          <TransactionList 
            limit={3} 
            showSearch={false} 
            showFilters={false} 
          />
        </div>
      )}

      {/* VOICE PAY Token Airdrop */}
      {isConnected && (
        <div className="bg-gradient-to-r from-yellow-500/20 to-orange-500/20 backdrop-blur-sm rounded-2xl p-6 border border-yellow-500/30">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-bold text-white">VOICE PAY Airdrop</h3>
              <p className="text-yellow-200 text-sm">Claim your free VOICE PAY tokens!</p>
            </div>
            <div className="w-12 h-12 bg-yellow-500 rounded-full flex items-center justify-center">
              <img src="/Voice-32x32.svg" alt="Voice" className="w-6 h-6" />
            </div>
          </div>
          <div className="space-y-3">
            <div className="flex items-center space-x-2 text-sm text-white/80">
              <span>1. Follow</span>
              <a href="https://x.com/Voice_agents" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300">@Voice_agents</a>
              <span>on X</span>
            </div>
            <div className="flex items-center space-x-2 text-sm text-white/80">
              <span>2. Get claim code from</span>
              <a href="https://t.me/VoiceBNB_bot" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300">Telegram Bot</a>
            </div>
            <div className="flex items-center space-x-2 text-sm text-white/80">
              <span>3. Claim your tokens below</span>
            </div>
          </div>
          <button
            onClick={() => setShowClaimModal(true)}
            className="w-full mt-4 py-3 bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white font-medium rounded-lg transition-all duration-200"
          >
            Claim Airdrop
          </button>
        </div>
      )}

      {/* Getting Started */}
      {!isConnected && (
        <div className="text-center">
          <div className="bg-gray-900/95 backdrop-blur-sm rounded-2xl p-6 border border-gray-700/50">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-yellow-500 flex items-center justify-center">
              <img src="/Voice-32x32.svg" alt="Voice" className="w-8 h-8" />
            </div>
            <h3 className="text-xl font-bold text-white mb-2">
              Connect Your Wallet
            </h3>
            <p className="text-white/70 mb-4">
              Connect your wallet to start making voice payments on BNB Chain
            </p>
            <div className="text-sm text-white/50">
              Supported: MetaMask on BNB Smart Chain
            </div>
          </div>
        </div>
      )}

      {/* Claim Airdrop Modal */}
      {showClaimModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowClaimModal(false)} />
          <div className="relative bg-gray-900/95 backdrop-blur-md rounded-2xl p-6 w-full max-w-sm border border-gray-700/50">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white">Claim VOICE PAY Airdrop</h2>
              <button
                onClick={() => setShowClaimModal(false)}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              >
                <img src="/Voice-32x32.svg" alt="Voice" className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-white text-sm font-medium mb-2">
                  Enter Claim Code
                </label>
                <input
                  type="text"
                  value={claimCode}
                  onChange={(e) => setClaimCode(e.target.value)}
                  placeholder="Enter your claim code from Telegram bot"
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                />
              </div>

              <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                <p className="text-blue-200 text-xs">
                  <strong>Note:</strong> Get your claim code by following @Voice_agents on X and messaging our Telegram bot.
                </p>
              </div>

              <button
                onClick={handleClaimAirdrop}
                disabled={isClaimingAirdrop || !claimCode.trim()}
                className="w-full py-3 bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-all duration-200 flex items-center justify-center space-x-2"
              >
                {isClaimingAirdrop ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    <span>Claiming...</span>
                  </>
                ) : (
                  <span>Claim Tokens</span>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </motion.div>
  )
}

export default HomePage